import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, Sparkles, Zap, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const FEATURES_FREE = [
  "Hasta 5 presupuestos al mes",
  "Hasta 5 facturas al mes",
  "8 plantillas básicas",
  "Asistente IA limitado",
  "Soporte por email",
];

const FEATURES_PRO = [
  "Presupuestos y facturas ilimitados",
  "Asistente IA ilimitado",
  "Envío por email con tracking",
  "PDF con tu marca personalizada",
  "Multi-usuario (próximamente)",
  "Soporte prioritario",
];

export default function Plans() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    setPolling(true);
    const poll = async () => {
      if (attempts >= 8) {
        setPolling(false);
        toast.error("No se pudo confirmar el pago. Si has pagado, refresca en unos segundos.");
        return;
      }
      attempts++;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setPolling(false);
          toast.success("¡Bienvenido a Pro! Tu cuenta se ha actualizado.");
          await refreshUser();
          setSearchParams({});
          return;
        }
        if (r.data.status === "expired") {
          setPolling(false);
          toast.error("La sesión de pago expiró.");
          setSearchParams({});
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  const upgrade = async () => {
    setLoading(true);
    try {
      const r = await api.post("/billing/checkout", {
        package_id: "pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar el pago");
      setLoading(false);
    }
  };

  const isPro = user?.is_pro;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="plans-page">
      <header className="text-center max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-3">Planes</div>
        <h1 className="font-display font-bold text-4xl lg:text-5xl tracking-tight mb-3">Elige el plan que mejor encaja.</h1>
        <p className="text-white/60">Empieza gratis y crece sin límites con Pro. Cancela cuando quieras.</p>
      </header>

      {polling && (
        <div className="max-w-md mx-auto p-4 rounded-lg border border-brand-cyan/40 bg-brand-cyan/10 text-center" data-testid="payment-polling">
          <div className="text-brand-cyan font-medium">Verificando pago...</div>
          <div className="text-xs text-white/60 mt-1">No cierres esta ventana.</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Free */}
        <div className="p-8 rounded-lg border border-white/10 bg-[#0A1128]/40" data-testid="plan-free">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-white/60" strokeWidth={1.5} />
            <h2 className="font-display font-bold text-2xl">Free</h2>
          </div>
          <div className="font-display font-black text-5xl mb-1">0€<span className="text-base font-normal text-white/50">/mes</span></div>
          <p className="text-sm text-white/50 mb-6">Para empezar y probar.</p>
          <ul className="space-y-2 mb-8 text-sm">
            {FEATURES_FREE.map((f, i) => (
              <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-white/40 flex-shrink-0 mt-0.5" /> <span className="text-white/70">{f}</span></li>
            ))}
          </ul>
          <Button disabled className="w-full bg-white/5 border border-white/10 text-white/40 cursor-default">Plan actual</Button>
        </div>

        {/* Pro */}
        <div className={`p-8 rounded-lg relative ${isPro ? "border-2 border-emerald-500/50 bg-emerald-500/5" : "border-2 border-brand-orange/60 bg-gradient-to-br from-brand-orange/10 via-transparent to-brand-cyan/5 glow-orange"}`} data-testid="plan-pro">
          <div className="absolute -top-3 left-8 px-3 py-0.5 rounded-full bg-brand-orange text-white text-[10px] uppercase tracking-widest font-bold">Recomendado</div>
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-5 h-5 text-brand-orange" strokeWidth={1.5} />
            <h2 className="font-display font-bold text-2xl">Pro</h2>
          </div>
          <div className="font-display font-black text-5xl mb-1">35€<span className="text-base font-normal text-white/50">/mes</span></div>
          <p className="text-sm text-white/50 mb-1">+ IVA · 42,35 €/mes total</p>
          <p className="text-sm text-white/50 mb-6">Para autónomos y pymes en serio.</p>
          <ul className="space-y-2 mb-8 text-sm">
            {FEATURES_PRO.map((f, i) => (
              <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-brand-orange flex-shrink-0 mt-0.5" /> <span>{f}</span></li>
            ))}
          </ul>
          {isPro ? (
            <Button disabled className="w-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 cursor-default"><Check className="w-4 h-4 mr-2" /> Eres Pro</Button>
          ) : (
            <Button data-testid="upgrade-btn" onClick={upgrade} disabled={loading} className="w-full bg-brand-orange hover:bg-brand-orange-hover text-white font-medium"><Zap className="w-4 h-4 mr-2" /> {loading ? "Redirigiendo..." : "Hazte Pro"}</Button>
          )}
        </div>
      </div>

      <div className="text-center text-xs text-white/40 max-w-lg mx-auto">
        Pago seguro con Stripe. Cancela en cualquier momento. Precio sin IVA: 35 €/mes (42,35 € IVA incluido).
      </div>
    </div>
  );
}
